import { useMemo, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";
import { Percent, Trophy } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtPct, fmtInt } from "@/lib/format";

// Cores rotativas para as vendedoras (usando chart-* vars do tema)
const LINE_COLORS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
  "#f59e0b",
  "#10b981",
  "#8b5cf6",
];

const MESES_PT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

const monthKey = (p) => {
  const raw = p.data_registro || p.created_at || "";
  return raw.slice(0, 7);
};

const monthLabel = (yyyyMm) => {
  const [y, m] = yyyyMm.split("-");
  const idx = parseInt(m, 10) - 1;
  return `${MESES_PT[idx] || m}/${y.slice(2)}`;
};

// Gera lista dos últimos N meses (YYYY-MM) até hoje inclusive
const lastNMonths = (n) => {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = n - 1; i >= 0; i--) {
    const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
    out.push(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
};

const quartersFrom = (months) => {
  // months: array YYYY-MM em ordem crescente
  const buckets = new Map();
  for (const ym of months) {
    const [y, m] = ym.split("-");
    const q = Math.floor((parseInt(m, 10) - 1) / 3) + 1;
    const key = `${y}-Q${q}`;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(ym);
  }
  return [...buckets.entries()].map(([label, months]) => ({ label, months }));
};

export default function AnaliseConversaoCard({ prospectos = [] }) {
  const [periodo, setPeriodo] = useState("6m"); // 3m | 6m | 12m | trimestre | total

  const monthsWindow = useMemo(() => {
    if (periodo === "3m") return lastNMonths(3);
    if (periodo === "6m") return lastNMonths(6);
    if (periodo === "12m") return lastNMonths(12);
    if (periodo === "trimestre") return lastNMonths(12); // agrupa em trimestres
    // total: usa todos os meses presentes nos dados
    const set = new Set();
    for (const p of prospectos) {
      const k = monthKey(p);
      if (k) set.add(k);
    }
    return [...set].sort();
  }, [periodo, prospectos]);

  const vendedoras = useMemo(() => {
    const set = new Set();
    for (const p of prospectos) {
      const v = (p.vendedor || "").trim();
      if (v) set.add(v);
    }
    return [...set].sort();
  }, [prospectos]);

  // dados p/ o gráfico: buckets (mês ou trimestre)
  const chartData = useMemo(() => {
    const useQuarter = periodo === "trimestre";
    const buckets = useQuarter
      ? quartersFrom(monthsWindow).map((q) => ({ label: q.label, months: q.months }))
      : monthsWindow.map((m) => ({ label: monthLabel(m), months: [m] }));

    return buckets.map((b) => {
      const monthSet = new Set(b.months);
      const row = { periodo: b.label };
      for (const v of vendedoras) {
        let total = 0;
        let conv = 0;
        for (const p of prospectos) {
          if ((p.vendedor || "").trim() !== v) continue;
          if (!monthSet.has(monthKey(p))) continue;
          total += 1;
          if (p.status === "convertido") conv += 1;
        }
        row[v] = total > 0 ? Number(((conv / total) * 100).toFixed(1)) : 0;
        row[`__total_${v}`] = total;
      }
      return row;
    });
  }, [monthsWindow, vendedoras, prospectos, periodo]);

  // ranking geral (janela completa)
  const ranking = useMemo(() => {
    const monthSet = new Set(monthsWindow);
    const map = new Map();
    for (const p of prospectos) {
      const v = (p.vendedor || "").trim();
      if (!v) continue;
      // Se período for "total", inclui tudo; senão filtra pela janela
      if (periodo !== "total") {
        if (!monthSet.has(monthKey(p))) continue;
      }
      const acc = map.get(v) || { vendedor: v, total: 0, conv: 0 };
      acc.total += 1;
      if (p.status === "convertido") acc.conv += 1;
      map.set(v, acc);
    }
    const arr = [...map.values()].map((r) => ({
      ...r,
      taxa: r.total > 0 ? (r.conv / r.total) * 100 : 0,
    }));
    arr.sort((a, b) => b.taxa - a.taxa);
    return arr;
  }, [prospectos, monthsWindow, periodo]);

  const totalGeral = ranking.reduce((s, r) => s + r.total, 0);
  const convGeral = ranking.reduce((s, r) => s + r.conv, 0);
  const taxaGeral = totalGeral > 0 ? (convGeral / totalGeral) * 100 : 0;

  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="analise-conversao-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <Percent className="w-4 h-4 text-primary" />
          <h3 className="font-heading font-semibold text-lg">Análise de conversão</h3>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">Prospectos → Clientes</Badge>
          <Select value={periodo} onValueChange={setPeriodo}>
            <SelectTrigger className="w-[180px]" data-testid="filter-conversao-periodo">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="3m">Últimos 3 meses</SelectItem>
              <SelectItem value="6m">Últimos 6 meses</SelectItem>
              <SelectItem value="12m">Últimos 12 meses</SelectItem>
              <SelectItem value="trimestre">Por trimestre (12m)</SelectItem>
              <SelectItem value="total">Total (todos)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Resumo */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <div className="rounded-lg border border-border p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Taxa geral</div>
          <div className="font-heading text-2xl font-extrabold text-primary tabular-nums mt-0.5">
            {fmtPct(taxaGeral)}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {fmtInt(convGeral)} convertidos · {fmtInt(totalGeral)} prospectos
          </div>
        </div>
        <div className="rounded-lg border border-border p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Melhor conversão</div>
          <div className="font-heading text-lg font-bold truncate mt-0.5">
            {ranking[0]?.vendedor || "—"}
          </div>
          <div className="text-xs text-emerald-500 mt-0.5 tabular-nums">
            {ranking[0] ? fmtPct(ranking[0].taxa) : "—"}
          </div>
        </div>
        <div className="rounded-lg border border-border p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Vendedoras ativas</div>
          <div className="font-heading text-2xl font-extrabold tabular-nums mt-0.5">
            {fmtInt(vendedoras.length)}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">com prospectos no período</div>
        </div>
      </div>

      {/* Gráfico */}
      <div className="h-72" data-testid="conversao-chart">
        {vendedoras.length === 0 || chartData.length === 0 ? (
          <div className="h-full grid place-items-center text-sm text-muted-foreground">
            Sem prospectos suficientes para calcular conversão.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={{ top: 10, right: 12, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="hsl(var(--border))" vertical={false} strokeDasharray="3 3" />
              <XAxis dataKey="periodo" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis
                stroke="hsl(var(--muted-foreground))"
                fontSize={12}
                tickFormatter={(v) => `${v}%`}
                domain={[0, 100]}
              />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(val, name, ctx) => {
                  const totalKey = `__total_${name}`;
                  const total = ctx?.payload?.[totalKey];
                  return [`${val}% (${total ?? 0} prosp.)`, name];
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {vendedoras.map((v, idx) => (
                <Line
                  key={v}
                  type="monotone"
                  dataKey={v}
                  stroke={LINE_COLORS[idx % LINE_COLORS.length]}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Ranking */}
      <div className="mt-6" data-testid="conversao-ranking">
        <div className="flex items-center gap-2 mb-3">
          <Trophy className="w-4 h-4 text-primary" />
          <h4 className="font-heading font-semibold">Ranking de conversão</h4>
        </div>
        <div className="space-y-2.5">
          {ranking.length === 0 && (
            <div className="text-sm text-muted-foreground">Sem dados no período.</div>
          )}
          {ranking.map((r, idx) => {
            const tone =
              r.taxa >= 50 ? "text-emerald-500" : r.taxa >= 25 ? "text-amber-500" : "text-red-500";
            return (
              <div key={r.vendedor} className="space-y-1" data-testid={`conversao-rank-${idx + 1}`}>
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary text-[11px] grid place-items-center font-semibold">
                      {idx + 1}
                    </span>
                    <span className="truncate">{r.vendedor}</span>
                  </div>
                  <span className={`text-xs font-semibold tabular-nums ${tone}`}>{fmtPct(r.taxa)}</span>
                </div>
                <Progress value={Math.min(r.taxa, 100)} className="h-1.5" />
                <div className="text-[11px] text-muted-foreground tabular-nums">
                  {fmtInt(r.conv)} convertidos de {fmtInt(r.total)} prospectos
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
